import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';

export const NeonSelect = ({ value, onChange, options, className = '', 'data-testid': testid, placeholder = 'Select…' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
      document.removeEventListener('keydown', k);
    };
  }, []);

  const current = options.find((o) => o.value === value);
  const pick = (v) => { onChange({ target: { value: v } }); setOpen(false); };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={`neon-select-trigger w-full flex items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-sm text-left text-white ${open ? 'is-open' : ''}`}>
        <span className="truncate">{current ? current.label : <span className="text-white/35">{placeholder}</span>}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
          <ChevronDown className="w-4 h-4 text-boosta-cyan" />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }} transition={{ duration: 0.18, ease: 'easeOut' }}
            className="neon-select-panel absolute left-0 right-0 mt-2 z-50 rounded-xl overflow-hidden"
            data-testid={testid ? `${testid}-panel` : undefined}>
            <div className="max-h-56 overflow-y-auto py-1.5">
              {options.map((o, i) => (
                <motion.button key={o.value} type="button"
                  data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.03 * i, duration: 0.15 }}
                  onClick={() => pick(o.value)}
                  className={`w-full flex items-center justify-between gap-2 px-3.5 py-2 text-sm text-left transition-colors ${
                    o.value === value ? 'text-boosta-cyan bg-boosta-cyan/10 font-semibold' : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
                  }`}>
                  <span className="truncate">{o.label}</span>
                  {o.value === value && <Check className="w-3.5 h-3.5 shrink-0" />}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NeonSelect;
